/**
 * The brand location detail block shared by BrandEditor's Create and Edit
 * modals: the "Country verified" lock that sits under the country picker, and
 * the four free-text location fields. The form state is one object so both
 * modals hold, reset, seed and save it the same way.
 */
import { useId } from 'react';
import type { WatchBrand } from '../../types';

export interface BrandLocationForm {
  countryVerified: boolean;
  city: string;
  stateRegion: string;
  assemblyLocation: string;
  designLocation: string;
}

export const LOCATION_TEXT_KEYS = [
  'city',
  'stateRegion',
  'assemblyLocation',
  'designLocation',
] as const satisfies readonly (keyof WatchBrand)[];

export const emptyBrandLocationForm: BrandLocationForm = {
  countryVerified: false,
  city: '',
  stateRegion: '',
  assemblyLocation: '',
  designLocation: '',
};

export function brandLocationFormFromBrand(brand: Partial<WatchBrand>): BrandLocationForm {
  return {
    countryVerified: brand.countryVerified === true,
    city: brand.city ?? '',
    stateRegion: brand.stateRegion ?? '',
    assemblyLocation: brand.assemblyLocation ?? '',
    designLocation: brand.designLocation ?? '',
  };
}

/** What a NEW brand writes: only what was filled in; nothing blank, no `false`. */
export function brandLocationCreatePayload(form: BrandLocationForm): Partial<WatchBrand> {
  const payload: Partial<WatchBrand> = {};
  for (const key of LOCATION_TEXT_KEYS) {
    const value = form[key].trim();
    if (value) payload[key] = value;
  }
  if (form.countryVerified) payload.countryVerified = true;
  return payload;
}

/**
 * What an EDIT writes. A blanked field that held a value is written as '' (or
 * `false` for the lock) so the clear actually persists — `undefined` would be
 * stripped before the update and leave the old value in place. A field that was
 * never set and is still blank is left out, so untouched brands are not
 * littered with empty keys.
 */
export function brandLocationUpdatePayload(
  form: BrandLocationForm,
  existing: WatchBrand,
): Partial<WatchBrand> {
  const payload: Partial<WatchBrand> = {};
  for (const key of LOCATION_TEXT_KEYS) {
    const value = form[key].trim();
    if (value) payload[key] = value;
    else if (existing[key]) payload[key] = '';
  }
  if (form.countryVerified) payload.countryVerified = true;
  else if (existing.countryVerified !== undefined) payload.countryVerified = false;
  return payload;
}

const TEXT_FIELDS: { key: (typeof LOCATION_TEXT_KEYS)[number]; label: string; placeholder: string }[] = [
  { key: 'city', label: 'City', placeholder: 'Headquarters city' },
  { key: 'stateRegion', label: 'State / region', placeholder: 'US state, UK county, province…' },
  { key: 'assemblyLocation', label: 'Assembly location', placeholder: 'Where the watches are assembled' },
  { key: 'designLocation', label: 'Design location', placeholder: 'Where the watches are designed' },
];

interface BrandLocationFieldsProps {
  value: BrandLocationForm;
  onChange: (next: BrandLocationForm) => void;
  disabled?: boolean;
}

export function BrandLocationFields({ value, onChange, disabled = false }: BrandLocationFieldsProps) {
  const id = useId();
  return (
    <div className='space-y-4'>
      <div>
        <div className='flex items-center gap-2'>
          <input
            type='checkbox'
            id={`${id}-countryVerified`}
            checked={value.countryVerified}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, countryVerified: e.target.checked })}
            className='rounded border-gray-300 text-brand-600 focus:ring-brand-500'
          />
          <label htmlFor={`${id}-countryVerified`} className='text-sm font-medium text-gray-700'>
            Country verified
          </label>
        </div>
        <p className='text-xs text-gray-500 mt-1'>
          Locks the country against Microbrand Atlas overwrites: while this is ticked, External
          Brand Proposals cannot include the MA country for this brand.
        </p>
      </div>

      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        {TEXT_FIELDS.map((field) => (
          <div key={field.key}>
            <label
              htmlFor={`${id}-${field.key}`}
              className='block text-sm font-medium text-gray-700 mb-1'
            >
              {field.label}
            </label>
            <input
              type='text'
              id={`${id}-${field.key}`}
              value={value[field.key]}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, [field.key]: e.target.value })}
              placeholder={field.placeholder}
              className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
            />
          </div>
        ))}
      </div>
    </div>
  );
}
